import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Modal, Button, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import DOMPurify from 'dompurify';
import { useTranslation } from 'react-i18next';
import { fetchLatestTermsAndConditions } from 'services/termsService';
import './TermsAndConditions.scss';

interface TermsAndConditionsProps {
  visible: boolean;
  onClose: () => void;
  onOK?: () => void;
  data?: any;
  closable?: boolean;
  maskClosable?: boolean;
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({
  visible,
  onClose,
  onOK,
  data,
  closable = false,
  maskClosable = false,
}) => {
  const { t, i18n } = useTranslation('common');
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchTerms = async () => {
      if (visible) {
        if (data) {
          const currentLang = i18n.language.startsWith('zh') ? 'zh' : 'en';
          const content = data?.content?.[currentLang] || data?.content?.en || '';
          setHtmlContent(content);
          return;
        }

        setLoading(true);
        try {
          const response = await fetchLatestTermsAndConditions();
          const resData = response.data;
          const currentLang = i18n.language.startsWith('zh') ? 'zh' : 'en';
          setHtmlContent(resData?.content?.[currentLang] || resData?.content?.en || '');
        } catch (error) {
          console.error('Failed to fetch terms:', error);
          setHtmlContent('<p>Failed to load terms and conditions.</p>');
        } finally {
          setLoading(false);
        }
      }
    };

    fetchTerms();
  }, [visible, i18n.language, data]);

  useEffect(() => {
  if (!loading && htmlContent && contentRef.current) {
    contentRef.current.scrollTop = 0;
    setIsScrolledToBottom(false);
  }
}, [loading, htmlContent, visible]);

  //prevent xss attack
  const cleanContent = useMemo(() => {
    if (!htmlContent) return '';

    const processedHtml = htmlContent.replace(/<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>/gi, '');

    return DOMPurify.sanitize(processedHtml, {
      ALLOWED_TAGS: [
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'p',
        'br',
        'strong',
        'em',
        'u',
        'a',
        'ul',
        'ol',
        'li',
        'blockquote',
        'code',
        'pre',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'img',
        'div',
        'span',
      ],
      ALLOWED_ATTR: [
        'href',
        'target',
        'rel',
        'class',
        'id',
        'src',
        'alt',
        'width',
        'height',
      ],
      KEEP_CONTENT: true,
    });
  }, [htmlContent]);

  // Scroll to top when modal opens
  const handleModalOpenChange = (isOpen: boolean) => {
    if (isOpen && contentRef.current) {
      contentRef.current.scrollTop = 0;
      setIsScrolledToBottom(false);
    }
  };

  // Check if content is scrolled to bottom
  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      // Allow 10px tolerance for floating point errors
      const isAtBottom = scrollHeight - scrollTop - clientHeight <= 10;
      setIsScrolledToBottom(isAtBottom);
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      closable={closable}
      maskClosable={maskClosable}
      afterOpenChange={handleModalOpenChange}
      footer={null}
      centered
      width={{
        xs: '90%',
        sm: '80%',
        md: '70%',
        lg: '60%',
        xl: '50%',
        xxl: '50%',
      }}
      closeIcon={<CloseOutlined />}
      className="terms-and-conditions-modal"
    >
      <div className="terms-and-conditions-content">
        {loading ? (
          <div className="terms-loading">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {/* HTML Content */}
            <div
              ref={contentRef}
              className="terms-html-content"
              onScroll={handleScroll}
              dangerouslySetInnerHTML={{ __html: cleanContent }}
            />

            {onOK && (
              <div className="terms-footer">
                <Button
                  type="primary"
                  block
                  onClick={onOK}
                  disabled={!isScrolledToBottom}
                  className="terms-ok-button"
                >
                  {t('button.ok')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default TermsAndConditions;